import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";
import Button from "../components/ui/Button";
import { CARTOMANCER_LOGO, DISCORD_LOGO, VTT_LOGO } from "../branding";
import SeoHead from "../components/SeoHead";

const HomePage = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen">
      <SeoHead
        title={t("home.seoTitle")}
        description={t("home.seoDesc")}
        path="/"
      />
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20 px-6 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-purple-600/10 rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-ruby/10 rounded-full blur-3xl animate-float" style={{ animationDelay: "2s" }}></div>
          <div className="absolute top-1/2 right-1/3 w-48 h-48 bg-gold/10 rounded-full blur-3xl animate-float" style={{ animationDelay: "4s" }}></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <div className="flex justify-center mb-12">
            <div className="animate-float">
              <div className="rune-border p-4 rounded-3xl bg-card/50 backdrop-blur gold-glow">
                <img
                  src="https://customer-assets-m6fa6gv7.emergentagent.net/job_3563a477-4b96-448c-b228-bf27feeae015/artifacts/a3flx1m9_logo-mytharion%20no%20nome.png"
                  alt="Mytharion Emblem"
                  className="w-96 h-96 object-contain animate-arcane-glow"
                />
              </div>
            </div>
          </div>

          <h1
            className="font-display text-7xl md:text-8xl lg:text-9xl mb-8 px-6 leading-none tracking-wider"
            style={{
              background: "linear-gradient(180deg, #d4af37 0%, #b8860b 50%, #d4af37 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              textShadow: "0 0 40px rgba(212, 175, 55, 0.3)",
            }}
          >
            MYTHARION
          </h1>

          <div className="flex items-center justify-center mb-8">
            <div className="h-px w-32 bg-gradient-to-r from-transparent to-gold"></div>
            <svg className="w-6 h-6 mx-4 text-gold" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2l2.4 7.2h7.6l-6 4.8 2.4 7.2-6-4.8-6 4.8 2.4-7.2-6-4.8h7.6z" />
            </svg>
            <div className="h-px w-32 bg-gradient-to-l from-transparent to-gold"></div>
          </div>

          <p className="font-heading text-sm md:text-base tracking-[0.3em] text-purple-300/90 mb-12 uppercase">
            {t("home.subtitle")}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <Link to="/product/cartomancer_basic" data-testid="home-cta-discover">
              <Button variant="primary" size="lg">
                {t("home.ctaDiscover")}
              </Button>
            </Link>
            <Link to="/register" data-testid="home-cta-register">
              <Button variant="secondary" size="lg">
                {t("home.ctaRegister")}
              </Button>
            </Link>
          </div>

          <div className="divider-fantasy max-w-md mx-auto"></div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-16">
            <p className="font-heading text-xs tracking-widest text-gold mb-4">
              {t("home.sectionEyebrow")}
            </p>
            <h2 className="font-display text-4xl md:text-5xl text-foreground mb-6">
              {t("home.sectionTitle")}
            </h2>
            <p className="font-serif-fantasy italic text-lg text-muted-foreground max-w-2xl mx-auto">
              {t("home.sectionQuote")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Cartomancer */}
            <div className="rune-border bg-card/80 backdrop-blur rounded-lg p-8 object-contain transition-all duration-700 ease-out hover:drop-shadow-[0_0_35px_rgba(147,51,234,0.9)] hover:scale-[1.02]" data-testid="home-card-cartomancer">
              <div className="mb-6">
                <div className="mb-4 h-24 flex items-center justify-center">
                  <img
                    src={CARTOMANCER_LOGO}
                    alt="Cartomancer"
                    className="max-h-full max-w-full object-contain transition-all duration-700 ease-out hover:drop-shadow-[0_0_30px_rgba(255,215,100,0.8)]"
                    data-testid="home-cartomancer-logo"
                  />
                </div>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {t("home.cartoDesc")}
                </p>
              </div>
              <Link to="/product/cartomancer_basic">
                <Button variant="ghost" size="sm" className="text-gold hover:text-gold/80">
                  {t("home.cartoCta")}
                </Button>
              </Link>
            </div>

            {/* Discord */}
            <div className="rune-border bg-card/80 backdrop-blur rounded-lg p-8 object-contain transition-all duration-700 ease-out hover:drop-shadow-[0_0_35px_rgba(147,51,234,0.9)] hover:scale-[1.02]" data-testid="home-card-discord">
              <div className="mb-6">
                <div className="mb-4 h-24 flex items-center justify-center">
                  <img
                    src={DISCORD_LOGO}
                    alt="Community Discord"
                    className="max-h-full max-w-full object-contain transition-all duration-700 ease-out hover:drop-shadow-[0_0_30px_rgba(255,215,100,0.8)]"
                    data-testid="home-discord-logo"
                  />
                </div>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {t("home.discordDesc")}
                </p>
              </div>
              <Link to="https://discord.gg/wZMc9RuCbr">
                <Button variant="ghost" size="sm" className="text-gold hover:text-gold/80">
                  {t("home.discordCta")}
                </Button>
              </Link>
            </div>

            {/* VTT */}
            <div className="rune-border bg-card/80 backdrop-blur rounded-lg p-8 object-contain transition-all duration-700 ease-out hover:drop-shadow-[0_0_35px_rgba(147,51,234,0.9)] hover:scale-[1.02]" data-testid="home-card-VTT">
              <div className="mb-6">
                <div className="mb-4 h-24 flex items-center justify-center">
                  <img
                    src={VTT_LOGO}
                    alt="MytharionVTT"
                    className="max-h-full max-w-full object-contain transition-all duration-700 ease-out hover:drop-shadow-[0_0_30px_rgba(255,215,100,0.8)]"
                    data-testid="home-vtt-logo"
                  />
                </div>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {t("home.vttDesc")}
                </p>
              </div>
              <Link to="/VTT">
                <Button variant="ghost" size="sm" className="text-gold hover:text-gold/80">
                  {t("home.vttCta")}
                </Button>
              </Link>
            </div>

            {/* Community Store */}
            <div className="rune-border bg-card/80 backdrop-blur rounded-lg p-8 opacity-60">
              <div className="mb-6">
                <div className="mb-4 flex items-center justify-center">
                  <div className="w-14 h-14 rounded-full bg-purple-500/20 flex items-center justify-center mb-4">
                    <Sparkles className="w-7 h-7 text-arcane" />
                  </div>
                </div>
                <h3 className="font-heading text-2xl text-purple-400 mb-3">
                  {t("home.communityTitle")}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {t("home.communityDesc")}
                </p>
              </div>
              <div className="inline-block px-3 py-1 bg-purple-500/20 border border-purple-500/30 rounded-full">
                <span className="font-heading text-xs text-purple-400">
                  {t("common.soon")}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="rune-border bg-card/50 backdrop-blur rounded-2xl p-12 gold-glow transition-all duration-700 ease-out hover:-translate-y-2 hover:scale-[1.14] hover:shadow-[0_0_30px_rgba(255,215,100,0.8)]">
            <h2 className="font-display text-4xl md:text-5xl text-gold mb-6 transition-all duration-700 hover:drop-shadow-[0_0_25px_rgba(168,85,247,0.8)]">
              {t("home.ctaSupportTitle")}
            </h2>
            <p className="font-serif-fantasy italic text-lg text-parchment/90 mb-8 max-w-2xl mx-auto">
              {t("home.ctaSupportQuote")}
            </p>
            <Link to="https://ko-fi.com/mytharion" data-testid="home-cta-donate">
              <Button variant="primary" size="lg" className="transition-all">
                {t("home.ctaSupportBtn")}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
